import React, { useState, useEffect, useCallback } from "react";
import { ReactFlowProvider } from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import WorkFlow from "../components/WorkFlow/index.js";
import NodesPanel from "../views/details/components/NodesPanel.js";
import Setting from "../views/details/components/Setting.js";

import useStore from "../components/WorkFlow/store/index.js";
import { ensureSingleStartNode } from "../components/WorkFlow/utils/index.js";
import { extractGreetingTemplateFromWorkflow } from "../lib/workflowGreeting.js";

function Workflow({ initialData = null }) {
  const [isLoading, setIsLoading] = useState(true);
  const [scriptName, setScriptName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [aiName, setAiName] = useState("Maya");
  const [saving, setSaving] = useState(false);

  const setAgent = useStore((state) => state.setAgent);
  const setNodes = useStore((state) => state.setNodes);
  const setEdges = useStore((state) => state.setEdges);

  // Get the current state from the store when needed
  const getCurrentWorkflowData = useCallback(() => {
    const storeState = useStore.getState();
    return {
      nodes: storeState.nodes,
      edges: storeState.edges,
      agent: storeState.agentSetting
    };
  }, []);

  useEffect(() => {
    if (initialData) {
      setNodes(ensureSingleStartNode(initialData.nodes || []));
      setEdges(initialData.edges || []);
      setAgent(initialData.agent || {});
    } else {
      setNodes([
        {
          id: "1",
          type: "start",
          data: { label: "Start" },
          position: { x: 50, y: 100 },
        },
      ]);
      setEdges([]);
      setAgent({});
    }
    setIsLoading(false);
  }, [initialData, setAgent, setNodes, setEdges]);

  const handleDownloadJSON = () => {
    const workflowData = getCurrentWorkflowData();
    const jsonString = JSON.stringify({
      nodes: workflowData.nodes,
      edges: workflowData.edges,
      agent: workflowData.agent
    }, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${scriptName || 'workflow'}-settings.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSaveScript = async () => {
    if (!scriptName.trim()) return alert("Please provide a script name.");

    const workflowData = getCurrentWorkflowData();
    const workflowJson = {
      nodes: workflowData.nodes,
      edges: workflowData.edges,
      agent: workflowData.agent,
    };

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
      const ownerId = currentUser?.id || currentUser?._id || currentUser?.userId || null;
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const res = await fetch(`${apiBase}/api/save-script`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
          body: JSON.stringify({
          scriptName,
          script: JSON.stringify(workflowJson, null, 2),
          firstName,
          lastName,
          businessName,
          aiName,
          type: "Conversation Flow",
          ownerId,
          greetingTemplate: extractGreetingTemplateFromWorkflow(workflowJson) || undefined,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        alert("✅ Script saved successfully!");
      } else {
        alert(`❌ Error saving script: ${data.message || "Unknown error."}`);
      }
    } catch (err) {
      console.error("❌ Save failed:", err);
      alert("❌ Failed to save script.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ReactFlowProvider>
      <div className="flex flex-col h-[calc(100vh-100px)] w-full">
        {/* Top bar with inputs */}
        <div className="bg-white shadow p-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-shrink-0">
          <input
            type="text"
            placeholder="Script Name"
            className="border rounded px-3 py-2 flex-1 text-sm md:text-base"
            value={scriptName}
            onChange={(e) => setScriptName(e.target.value)}
          />
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <input
              type="text"
              placeholder="First Name"
              className="border rounded px-3 py-2 w-full sm:w-[160px] text-sm md:text-base"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <input
              type="text"
              placeholder="Last Name"
              className="border rounded px-3 py-2 w-full sm:w-[160px] text-sm md:text-base"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <input
              type="text"
              placeholder="Business Name"
              className="border rounded px-3 py-2 w-full sm:w-[200px] text-sm md:text-base"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
            />
            <input
              type="text"
              placeholder="AI Name (e.g., Maya)"
              className="border rounded px-3 py-2 w-full sm:w-[160px] text-sm md:text-base"
              value={aiName}
              onChange={(e) => setAiName(e.target.value)}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleDownloadJSON}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 md:px-4 py-2 rounded text-sm md:text-base"
            >
              💾 Save as JSON
            </button>
            <button
              onClick={handleSaveScript}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white px-3 md:px-4 py-2 rounded disabled:opacity-50 text-sm md:text-base"
            >
              {saving ? "Saving..." : "Save Script"}
            </button>
          </div>
        </div>

        {/* Main workflow editor */}
        <main className="flex-1 flex flex-col lg:flex-row bg-[#F5F7FA] overflow-hidden min-h-0">
          <div className="flex-1 relative min-w-0 min-h-0">
            <div className="absolute left-3 top-3 z-10">
              <NodesPanel variant="dropdown" />
            </div>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
                <div className="text-lg font-semibold text-gray-700">
                  Loading...
                </div>
              </div>
            )}
            <WorkFlow
              isLoading={isLoading}
              nodesData={initialData ? ensureSingleStartNode(initialData.nodes || []) : []}
              edgesData={initialData ? initialData.edges || [] : []}
            />
          </div>
          <div className="hidden lg:block">
            <Setting />
          </div>
        </main>
      </div>
    </ReactFlowProvider>
  );
}

export default Workflow;
